import { useState, useCallback } from 'react';

/**
 * ページナビゲーションを管理するカスタムフック
 * 
 * PDFビューアのページ移動、ページ入力の管理を行う。
 * ズーム操作は useZoomControls フックで管理する。
 * 
 * @param {number} initialPage - 初期ページ番号（デフォルト: 1）
 * @returns {Object} ページナビゲーション関連の状態とハンドラ
 * @returns {number} returns.currentPage - 現在のページ番号
 * @returns {number} returns.totalPages - PDFの総ページ数
 * @returns {boolean} returns.isRendering - ページレンダリング中かどうか
 * @returns {Function} returns.setCurrentPage - 現在ページを設定する関数
 * @returns {Function} returns.setTotalPages - 総ページ数を設定する関数
 * @returns {Function} returns.setIsRendering - レンダリング状態を設定する関数
 * @returns {Function} returns.handlePageChange - ページ変更ハンドラ
 * @returns {Function} returns.handlePrevPage - 前ページへ移動するハンドラ
 * @returns {Function} returns.handleNextPage - 次ページへ移動するハンドラ
 * @returns {Function} returns.handleChangePageInput - ページ入力変更ハンドラ
 * @returns {Function} returns.handlePageInputBlur - ページ入力フォーカスアウトハンドラ
 * @returns {Function} returns.jumpToPage - 指定ページへジャンプする関数
 * @returns {Function} returns.isJumpPageValid - ジャンプ可否を判定する関数
 * @returns {Function} returns.parsePageNumber - ページ番号をパースするユーティリティ
 */
export const usePageNavigation = (initialPage = 1) => {
  /**
   * 現在表示中のページ番号
   */
  const [currentPage, setCurrentPage] = useState(initialPage);

  /**
   * PDFの総ページ数
   * PDFロード完了後に設定される
   */
  const [totalPages, setTotalPages] = useState(0);

  /**
   * ページレンダリング中フラグ
   * レンダリング中は連続ページ移動を防止する
   */
  const [isRendering, setIsRendering] = useState(false);

  /**
   * 指定ページへ移動
   * 有効範囲（1〜totalPages）内の場合のみ移動
   * 
   * @param {number} newPage - 移動先のページ番号
   */
  const handlePageChange = useCallback((newPage) => {

    // 同じページへの移動をスキップ
    if (newPage === currentPage) return;

    if (newPage >= 1 && newPage <= totalPages) {
      setIsRendering(true);
      setCurrentPage(newPage);
    }
  }, [currentPage, totalPages]);

  /**
   * 前のページへ移動
   * レンダリング中は無視される
   */
  const handlePrevPage = useCallback(() => {
    if (!isRendering) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, isRendering, handlePageChange]);

  /**
   * 次のページへ移動
   * レンダリング中は無視される
   */
  const handleNextPage = useCallback(() => {
    if (!isRendering) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, isRendering, handlePageChange]);

  /**
   * ページ番号をパースするユーティリティ
   * 数値または数値文字列を受け取り、有効なページ番号を返す
   * 無効な値の場合はNaNを返す
   * 
   * @param {number|string} rawPage - パース対象の値
   * @returns {number} パースされたページ番号（無効な場合はNaN）
   */
  const parsePageNumber = useCallback((rawPage) => {
    
    // 数値の場合、有限数かチェックして返す
    if (typeof rawPage === 'number') {
      return Number.isFinite(rawPage) ? rawPage : NaN;
    }

    // 文字列の場合
    if (typeof rawPage === 'string') {
      // 空文字かチェック
      const trimmed = rawPage.trim();
      if (trimmed === '') return NaN;
      
      // 数値文字列(整数)かチェック
      if (!/^\d+$/.test(trimmed)) return NaN;
      return Number(trimmed);
    }
    return NaN;
  }, []);

  /**
   * 指定ページへジャンプ
   * 有効なページ番号の場合のみジャンプ
   * 
   * @param {number|string} rawPage - ジャンプ先のページ番号
   */
  const jumpToPage = useCallback((rawPage) => {
    // ページ番号をパース
    const num = parsePageNumber(rawPage);
    if (!Number.isFinite(num)) return;

    // 有効範囲内かチェック
    if (num < 1 || (totalPages > 0 && num > totalPages)) return;

    // 指定のページへ移動
    handlePageChange(num);

  }, [parsePageNumber, totalPages, handlePageChange]);

  /**
   * 指定フィールドのページへジャンプ可能かを判定
   * detailResultの指定フィールドが有効なページ番号を持つかチェック
   * 
   * @param {Object} detailResult - 検索結果の詳細データ
   * @param {string} fieldName - チェックするフィールド名
   * @returns {boolean} ジャンプ可能な場合はtrue
   */
  const isJumpPageValid = useCallback((detailResult, fieldName) => {
    // detailResultまたはフィールドが存在しない場合はfalse
    if (!detailResult) return false;
    if (!Object.prototype.hasOwnProperty.call(detailResult, fieldName)) return false;

    // フィールドの値をページ番号としてパース
    const num = parsePageNumber(detailResult[fieldName]);
    
    // 有効なページ番号かチェック
    if (!Number.isFinite(num)) return false;
    if (num < 1) return false;
    if (totalPages > 0 && num > totalPages) return false;

    return true;

}, [parsePageNumber, totalPages]);

  /**
   * ページ入力欄の変更ハンドラ
   * 数値入力のみ受け付け、レンダリング中は無視
   * 
   * @param {Event} e - 入力イベント
   */
  const handleChangePageInput = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    if (!Number.isNaN(val) && !isRendering) {
      setCurrentPage(val);
    }
  }, [isRendering]);

  /**
   * ページ入力欄のフォーカスアウトハンドラ
   * 範囲外の値を有効範囲（1〜totalPages）に補正する
   */
  const handlePageInputBlur = useCallback(() => {
    if (currentPage < 1) setCurrentPage(1);
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  return {
    // 状態
    currentPage,
    totalPages,
    isRendering,
    // 状態設定関数
    setCurrentPage,
    setTotalPages,
    setIsRendering,
    // ページ移動ハンドラ
    handlePageChange,
    handlePrevPage,
    handleNextPage,
    // ページ入力ハンドラ
    handleChangePageInput,
    handlePageInputBlur,
    // ジャンプ関連
    jumpToPage,
    isJumpPageValid,
    parsePageNumber,
  };
};

export default usePageNavigation;
